import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth, type UserRole } from '@/context/AuthContext';
import { useToast } from '@/components/feature/Toast';
import DeleteConfirmModal from '@/components/feature/DeleteConfirmModal';

interface Usuario {
  id: string;
  nombre: string;
  usuario: string;
  rol: UserRole;
  created_at: string;
}

interface UsuarioForm {
  nombre: string;
  usuario: string;
  contraseña: string;
  rol: UserRole;
}

const emptyForm: UsuarioForm = {
  nombre: '',
  usuario: '',
  contraseña: '',
  rol: 'usuario',
};

export default function UsuariosPage() {
  const { user: currentUser } = useAuth();
  const { addToast } = useToast();

  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<Usuario | null>(null);
  const [form, setForm] = useState<UsuarioForm>(emptyForm);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [formLoading, setFormLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingUser, setDeletingUser] = useState<Usuario | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    loadUsuarios();
  }, []);

  const loadUsuarios = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('usuarios')
        .select('id, nombre, usuario, rol, created_at')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUsuarios((data as Usuario[]) || []);
    } catch {
      addToast('Error al cargar usuarios', 'error');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return usuarios;
    const q = search.toLowerCase();
    return usuarios.filter(
      (u) =>
        u.nombre.toLowerCase().includes(q) ||
        u.usuario.toLowerCase().includes(q) ||
        u.rol.toLowerCase().includes(q)
    );
  }, [usuarios, search]);

  const handleCreate = () => {
    setEditingUser(null);
    setForm(emptyForm);
    setFormErrors({});
    setShowPassword(false);
    setFormOpen(true);
  };

  const handleEdit = (usuario: Usuario) => {
    setEditingUser(usuario);
    setForm({
      nombre: usuario.nombre,
      usuario: usuario.usuario,
      contraseña: '',
      rol: usuario.rol,
    });
    setFormErrors({});
    setShowPassword(false);
    setFormOpen(true);
  };

  const handleDeleteClick = (usuario: Usuario) => {
    if (usuario.id === currentUser?.id) {
      addToast('No puedes eliminar tu propio usuario', 'warning');
      return;
    }
    setDeletingUser(usuario);
    setDeleteOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingUser) return;
    setDeleteLoading(true);
    try {
      const { error } = await supabase
        .from('usuarios')
        .delete()
        .eq('id', deletingUser.id);

      if (error) throw error;

      await supabase.from('auditoria').insert({
        usuario: currentUser?.nombre || 'Sistema',
        accion: `Eliminó al usuario: ${deletingUser.nombre} (${deletingUser.usuario})`,
        modulo: 'Usuarios',
      });

      addToast('Usuario eliminado correctamente', 'success');
      setDeleteOpen(false);
      setDeletingUser(null);
      loadUsuarios();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error al eliminar usuario';
      addToast(message, 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const errs: Record<string, string> = {};
    if (!form.nombre.trim()) errs.nombre = 'El nombre es requerido';
    if (!form.usuario.trim()) errs.usuario = 'El usuario es requerido';
    if (!editingUser && !form.contraseña.trim()) errs.contraseña = 'La contraseña es requerida';
    if (form.contraseña && form.contraseña.length < 6) errs.contraseña = 'Mínimo 6 caracteres';
    if (!['super_admin', 'usuario'].includes(form.rol)) errs.rol = 'Rol inválido';
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setFormLoading(true);
    try {
      if (editingUser) {
        const { data, error } = await supabase.rpc('actualizar_usuario', {
          p_id: editingUser.id,
          p_nombre: form.nombre.trim(),
          p_usuario: form.usuario.trim(),
          p_contraseña: form.contraseña || null,
          p_rol: form.rol,
        });

        if (error) throw error;
        const result = data as { success: boolean; error?: string };

        if (!result.success) {
          addToast(result.error || 'Error al actualizar usuario', 'error');
          return;
        }

        await supabase.from('auditoria').insert({
          usuario: currentUser?.nombre || 'Sistema',
          accion: `Editó al usuario: ${form.nombre.trim()} (${form.usuario.trim()})`,
          modulo: 'Usuarios',
        });

        addToast('Usuario actualizado correctamente', 'success');
      } else {
        const { data, error } = await supabase.rpc('crear_usuario', {
          p_nombre: form.nombre.trim(),
          p_usuario: form.usuario.trim(),
          p_contraseña: form.contraseña,
          p_rol: form.rol,
        });

        if (error) throw error;
        const result = data as { success: boolean; error?: string };

        if (!result.success) {
          addToast(result.error || 'Error al crear usuario', 'error');
          return;
        }

        await supabase.from('auditoria').insert({
          usuario: currentUser?.nombre || 'Sistema',
          accion: `Creó al usuario: ${form.nombre.trim()} (${form.usuario.trim()})`,
          modulo: 'Usuarios',
        });

        addToast('Usuario creado correctamente', 'success');
      }

      setFormOpen(false);
      loadUsuarios();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error al guardar usuario';
      addToast(message, 'error');
    } finally {
      setFormLoading(false);
    }
  };

  const getRolBadge = (rol: UserRole) => {
    if (rol === 'super_admin') {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
          Super Admin
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700">
        Usuario
      </span>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground-800">Usuarios</h1>
          <p className="text-foreground-500 text-sm mt-1">
            {usuarios.length} usuario{usuarios.length !== 1 ? 's' : ''} registrado{usuarios.length !== 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-user-add-line"></i>
          <span>Nuevo Usuario</span>
        </button>
      </div>

      <div className="bg-white rounded-xl border border-background-200 p-4">
        <div className="relative">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre, usuario o rol..."
            className="w-full pl-10 pr-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-foreground-800 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
          />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-3">
              <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
              <p className="text-foreground-500 text-sm">Cargando usuarios...</p>
            </div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-background-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <i className="ri-user-search-line text-3xl text-foreground-300"></i>
            </div>
            <h3 className="text-lg font-semibold text-foreground-600 mb-1">Sin usuarios</h3>
            <p className="text-foreground-400 text-sm">
              {search ? 'No se encontraron usuarios con ese criterio' : 'Crea el primer usuario para comenzar'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-background-200 bg-background-50">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Usuario</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider hidden sm:table-cell">Nombre de Usuario</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Rol</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider hidden md:table-cell">Creado</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => (
                  <tr key={u.id} className="border-b border-background-100 hover:bg-background-50/50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          u.id === currentUser?.id ? 'bg-primary-500' : 'bg-foreground-700'
                        }`}>
                          <span className="text-white text-xs font-semibold">{u.nombre.charAt(0).toUpperCase()}</span>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-foreground-800">{u.nombre}</span>
                          {u.id === currentUser?.id && (
                            <span className="ml-2 text-[10px] text-primary-600 font-medium">(Tú)</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <span className="text-sm text-foreground-600 font-mono">{u.usuario}</span>
                    </td>
                    <td className="px-4 py-3">{getRolBadge(u.rol)}</td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span className="text-sm text-foreground-500">
                        {new Date(u.created_at).toLocaleDateString('es-ES', { year: 'numeric', month: 'short', day: 'numeric' })}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleEdit(u)}
                          className="p-2 rounded-lg text-foreground-400 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer"
                          title="Editar usuario"
                        >
                          <i className="ri-edit-line text-lg"></i>
                        </button>
                        <button
                          onClick={() => handleDeleteClick(u)}
                          disabled={u.id === currentUser?.id}
                          className={`p-2 rounded-lg transition-colors cursor-pointer ${
                            u.id === currentUser?.id
                              ? 'text-foreground-200 cursor-not-allowed'
                              : 'text-foreground-400 hover:text-accent-600 hover:bg-accent-50'
                          }`}
                          title={u.id === currentUser?.id ? 'No puedes eliminarte a ti mismo' : 'Eliminar usuario'}
                        >
                          <i className="ri-delete-bin-line text-lg"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {formOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 animate-fade-in" onClick={() => setFormOpen(false)} />
          <div className="relative bg-white rounded-2xl w-full max-w-md animate-scale-in">
            <div className="flex items-center justify-between px-6 py-4 border-b border-background-200">
              <h2 className="font-heading text-lg font-semibold text-foreground-800">
                {editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
              </h2>
              <button
                onClick={() => setFormOpen(false)}
                className="p-2 rounded-lg text-foreground-400 hover:text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <form onSubmit={handleFormSubmit} className="p-6 space-y-4">
              <div>
                <label htmlFor="user-nombre" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  Nombre <span className="text-accent-500">*</span>
                </label>
                <input
                  id="user-nombre"
                  type="text"
                  value={form.nombre}
                  onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                  className={`w-full px-4 py-2.5 bg-background-50 border rounded-lg text-foreground-800 text-sm focus:outline-none focus:ring-2 transition-all ${
                    formErrors.nombre ? 'border-accent-400 focus:ring-accent-100' : 'border-background-200 focus:border-primary-400 focus:ring-primary-100'
                  }`}
                  placeholder="Nombre completo"
                />
                {formErrors.nombre && <p className="text-xs text-accent-500 mt-1">{formErrors.nombre}</p>}
              </div>

              <div>
                <label htmlFor="user-usuario" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  Usuario <span className="text-accent-500">*</span>
                </label>
                <input
                  id="user-usuario"
                  type="text"
                  value={form.usuario}
                  onChange={(e) => setForm({ ...form, usuario: e.target.value })}
                  className={`w-full px-4 py-2.5 bg-background-50 border rounded-lg text-foreground-800 text-sm focus:outline-none focus:ring-2 transition-all ${
                    formErrors.usuario ? 'border-accent-400 focus:ring-accent-100' : 'border-background-200 focus:border-primary-400 focus:ring-primary-100'
                  }`}
                  placeholder="Nombre de usuario"
                />
                {formErrors.usuario && <p className="text-xs text-accent-500 mt-1">{formErrors.usuario}</p>}
              </div>

              <div>
                <label htmlFor="user-password" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  Contraseña {!editingUser && <span className="text-accent-500">*</span>}
                </label>
                <div className="relative">
                  <input
                    id="user-password"
                    type={showPassword ? 'text' : 'password'}
                    value={form.contraseña}
                    onChange={(e) => setForm({ ...form, contraseña: e.target.value })}
                    className={`w-full px-4 pr-12 py-2.5 bg-background-50 border rounded-lg text-foreground-800 text-sm focus:outline-none focus:ring-2 transition-all ${
                      formErrors.contraseña ? 'border-accent-400 focus:ring-accent-100' : 'border-background-200 focus:border-primary-400 focus:ring-primary-100'
                    }`}
                    placeholder={editingUser ? 'Dejar vacío para mantener' : 'Contraseña'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer p-1"
                    tabIndex={-1}
                  >
                    <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                  </button>
                </div>
                {formErrors.contraseña && <p className="text-xs text-accent-500 mt-1">{formErrors.contraseña}</p>}
                {editingUser && <p className="text-xs text-foreground-400 mt-1">Deja en blanco para mantener la contraseña actual</p>}
              </div>

              <div>
                <label htmlFor="user-rol" className="block text-sm font-medium text-foreground-700 mb-1.5">Rol</label>
                <select
                  id="user-rol"
                  value={form.rol}
                  onChange={(e) => setForm({ ...form, rol: e.target.value as UserRole })}
                  className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-foreground-800 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all cursor-pointer"
                >
                  <option value="super_admin">Super Admin</option>
                  <option value="usuario">Usuario</option>
                </select>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setFormOpen(false)}
                  className="flex-1 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-700 font-medium rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap text-sm"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={formLoading}
                  className="flex-1 py-2.5 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white font-medium rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap text-sm flex items-center justify-center gap-2"
                >
                  {formLoading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      <span>Guardando...</span>
                    </>
                  ) : (
                    <>
                      <i className="ri-save-line"></i>
                      <span>{editingUser ? 'Actualizar' : 'Crear Usuario'}</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <DeleteConfirmModal
        open={deleteOpen}
        onClose={() => { setDeleteOpen(false); setDeletingUser(null); }}
        onConfirm={handleDeleteConfirm}
        loading={deleteLoading}
        title="Eliminar Usuario"
        message={`¿Estás seguro de eliminar a "${deletingUser?.nombre}"? Esta acción no se puede deshacer.`}
      />
    </div>
  );
}